import React, { useState, useEffect } from "react";
import { withAuthenticationRequired, useAuth0 } from "@auth0/auth0-react";
import { useHistory, useParams } from "react-router-dom";
import { Button, Card, CardBody, CardHeader, Alert, Table, Badge } from "reactstrap";
import Loading from "../components/Loading";
import RoleBadge from "../components/RoleBadge";
import { getOrganisation, deleteOrganisation } from "../utils/apiService";
import { canEdit, canDelete, canManageMembers } from "../utils/orgRoles";
import { BUSINESS_TYPES } from "../utils/orgConstants";

const OrganisationDetailsComponent = () => {
  const { id } = useParams();
  const history = useHistory();
  const { getAccessTokenSilently } = useAuth0();

  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadOrganisation();
  }, [id]);

  const loadOrganisation = async () => {
    setLoading(true);
    setError("");
    try {
      const token = await getAccessTokenSilently().catch(() => null);
      const data = await getOrganisation(id, token);

      // Normalize the response
      const normalized = {
        id: data?.id || data?.organisationId || id,
        name: data?.name || data?.orgName || data?.organisationName || 'Unknown',
        identifier: data?.identifier || data?.orgIdentifier || '',
        latitude: data?.latitude || 0,
        longitude: data?.longitude || 0,
        businessType: data?.businessType || 1,
        memberCount: data?.memberCount || data?.members?.length || 0,
        members: data?.members || [],
        userRole: data?.userRole || data?.role || null,
        createdAt: data?.createdAt || null,
      };

      setOrg(normalized);
    } catch (err) {
      setError(err?.message || "Failed to load organisation");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete "${org.name}"? This action cannot be undone.`)) {
      return;
    }

    setDeleting(true);
    setError("");
    try {
      const token = await getAccessTokenSilently().catch(() => null);
      await deleteOrganisation(id, token);
      history.push("/organisations");
    } catch (err) {
      setError(err?.message || "Failed to delete organisation");
      setDeleting(false);
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (error && !org) {
    return (
      <Alert color="danger">
        {error}
        <div className="mt-3">
          <Button color="secondary" onClick={() => history.push("/organisations")}>
            Back to Organisations
          </Button>
        </div>
      </Alert>
    );
  }

  if (!org) {
    return <Alert color="warning">Organisation not found</Alert>;
  }

  const businessTypeLabel = BUSINESS_TYPES.find(t => t.id === org.businessType)?.label || 'Unknown';

  return (
    <div>
      {error && <Alert color="danger">{error}</Alert>}

      <Card>
        <CardHeader className="d-flex justify-content-between align-items-center">
          <div>
            <h1 className="mb-0">
              {org.name}
              <RoleBadge role={org.userRole} />
            </h1>
            {org.identifier && (
              <div className="text-muted mt-1">
                <small>Identifier: {org.identifier}</small>
              </div>
            )}
          </div>
        </CardHeader>
        <CardBody>
          <Table borderless>
            <tbody>
              <tr>
                <th style={{ width: '200px' }}>Location</th>
                <td>
                  {org.latitude !== 0 || org.longitude !== 0 ? (
                    `${org.latitude}, ${org.longitude}`
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </td>
              </tr>
              <tr>
                <th>Business Type</th>
                <td>{businessTypeLabel}</td>
              </tr>
              <tr>
                <th>Member Count</th>
                <td>{org.memberCount}</td>
              </tr>
              {org.createdAt && (
                <tr>
                  <th>Created</th>
                  <td>{new Date(org.createdAt).toLocaleDateString()}</td>
                </tr>
              )}
            </tbody>
          </Table>

          {org.members && org.members.length > 0 && (
            <div className="mt-4">
              <h5>Members</h5>
              <Table striped size="sm">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                  </tr>
                </thead>
                <tbody>
                  {org.members.slice(0, 5).map((member, idx) => (
                    <tr key={idx}>
                      <td>{member.firstName} {member.lastName}</td>
                      <td>{member.email || member.emailAddress}</td>
                      <td><RoleBadge role={member.role} /></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {org.members.length > 5 && (
                <small className="text-muted">
                  Showing 5 of {org.members.length} members.{" "}
                  {canManageMembers(org.userRole) && (
                    <a href="#" onClick={(e) => { e.preventDefault(); history.push(`/organisations/${id}/members`); }}>
                      View all
                    </a>
                  )}
                </small>
              )}
            </div>
          )}

          <div className="d-flex gap-2 mt-4">
            <Button color="secondary" onClick={() => history.push("/organisations")}>
              Back to List
            </Button>
            {canEdit(org.userRole) && (
              <Button color="primary" onClick={() => history.push(`/organisations/${id}/edit`)}>
                <i className="fas fa-edit me-2"></i>Edit
              </Button>
            )}
            {canManageMembers(org.userRole) && (
              <Button color="info" onClick={() => history.push(`/organisations/${id}/members`)}>
                <i className="fas fa-users me-2"></i>Manage Members
              </Button>
            )}
            {canEdit(org.userRole) && (
              <Button color="success" onClick={() => history.push(`/organisations/${id}/offers`)}>
                <i className="fas fa-tag me-2"></i>Offers
              </Button>
            )}
            {canDelete(org.userRole) && (
              <Button color="danger" onClick={handleDelete} disabled={deleting}>
                <i className="fas fa-trash me-2"></i>
                {deleting ? 'Deleting...' : 'Delete'}
              </Button>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default withAuthenticationRequired(OrganisationDetailsComponent, {
  onRedirecting: () => <Loading />,
});
